import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { FetchReportByDate_api, FetchUsers_api, FetchCustomerData_api } from "../../Api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download, SlidersHorizontal, RotateCcw, User } from "lucide-react";
import { Spin, DatePicker, Select, Input, Drawer, Space, Button as AntdButton } from "antd";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { RangePicker } = DatePicker;
const { Option } = Select;

const METHOD_OPTIONS = [
  { value: "", label: "All Methods" },
  { value: "Direct", label: "Direct" },
  { value: "Remittance", label: "Remittance" },
  { value: "Dealer Payment", label: "Dealer Payment" },
];

export const ReportByDate = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BY_DATE,
    ["admin", "cashier", "manager"]
  );

  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [addedUserId, setAddedUserId] = useState<number | undefined>(undefined);
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  const [inquiryNumber, setInquiryNumber] = useState("");
  const [engineNumber, setEngineNumber] = useState("");
  const [chassisNumber, setChassisNumber] = useState("");
  const [slipId, setSlipId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftAddedUserId, setDraftAddedUserId] = useState<number | undefined>(undefined);
  const [draftCustomerId, setDraftCustomerId] = useState<number | undefined>(undefined);
  const [draftInquiryNumber, setDraftInquiryNumber] = useState("");
  const [draftEngineNumber, setDraftEngineNumber] = useState("");
  const [draftChassisNumber, setDraftChassisNumber] = useState("");
  const [draftSlipId, setDraftSlipId] = useState("");
  const [draftPaymentMethod, setDraftPaymentMethod] = useState("");

  const [users, setUsers] = useState<any[]>([]);
  const [customerData, setCustomerData] = useState<any[]>([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedCustomerSearch] = useDebounce(customerSearch, 500);

  useEffect(() => {
    if (hasPermission) {
      FetchUsers_api(1, 1000, "").then((response: any) => {
        if (response && response.users) {
          setUsers(response.users);
        }
      });
    }
  }, [hasPermission]);

  useEffect(() => {
    const fetchCustomers = async () => {
      const response: any = await FetchCustomerData_api(debouncedCustomerSearch.trim());
      if (response && response.customerData) {
        setCustomerData(response.customerData);
      }
    };
    fetchCustomers();
  }, [debouncedCustomerSearch]);

  const fetchReport = async (
    start: string,
    end: string,
    addedUser?: number,
    custId?: number,
    inqNo: string = "",
    engNo: string = "",
    chasNo: string = "",
    slip: string = "",
    method: string = "",
  ) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    const response = await FetchReportByDate_api({
      startDate: start,
      endDate: end,
      addedUser,
      customerId: custId,
      inquiryNumber: inqNo,
      engineNumber: engNo,
      chassisNumber: chasNo,
      slipId: slip,
      paymentMethod: method,
    });
    if (response.success) {
      setReportData(response.data);
      setTotalAmount(Number(response.total) || 0);
    } else {
      alert(response.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDraftAddedUserId(addedUserId);
    setDraftCustomerId(customerId);
    setDraftInquiryNumber(inquiryNumber);
    setDraftEngineNumber(engineNumber);
    setDraftChassisNumber(chassisNumber);
    setDraftSlipId(slipId);
    setDraftPaymentMethod(paymentMethod);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setAddedUserId(draftAddedUserId);
    setCustomerId(draftCustomerId);
    setInquiryNumber(draftInquiryNumber.trim());
    setEngineNumber(draftEngineNumber.trim());
    setChassisNumber(draftChassisNumber.trim());
    setSlipId(draftSlipId.trim());
    setPaymentMethod(draftPaymentMethod);
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(
      s,
      e,
      draftAddedUserId,
      draftCustomerId,
      draftInquiryNumber.trim(),
      draftEngineNumber.trim(),
      draftChassisNumber.trim(),
      draftSlipId.trim(),
      draftPaymentMethod,
    );
  };

  const resetFilters = () => {
    setDraftRange(null);
    setDraftAddedUserId(undefined);
    setDraftCustomerId(undefined);
    setDraftInquiryNumber("");
    setDraftEngineNumber("");
    setDraftChassisNumber("");
    setDraftSlipId("");
    setDraftPaymentMethod("");
    setStartDate("");
    setEndDate("");
    setAddedUserId(undefined);
    setCustomerId(undefined);
    setInquiryNumber("");
    setEngineNumber("");
    setChassisNumber("");
    setSlipId("");
    setPaymentMethod("");
    setReportData([]);
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const calculateTotal = () => {
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(totalAmount);
  };

  const countByMethod = () => {
    const counts: Record<string, number> = {};
    reportData.forEach((r) => {
      const m = r.payment_method || "Other";
      counts[m] = (counts[m] || 0) + 1;
    });
    return counts;
  };

  const toNumber = (value: any): number =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;

  const formatCurrency = (value: any) =>
    new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(toNumber(value));

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const activeUserName = users.find((u) => u.id === addedUserId)?.username || "";
  const activeCustomerName = customerData.find((c) => c.id === customerId)?.name || "";
  const filterSummary = [
    periodLabel,
    activeUserName,
    activeCustomerName,
    inquiryNumber ? `Inquiry: ${inquiryNumber}` : "",
    engineNumber ? `Engine: ${engineNumber}` : "",
    chassisNumber ? `Chassis: ${chassisNumber}` : "",
    slipId ? `Slip: ${slipId}` : "",
    paymentMethod || "",
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Added By",
      "Customer",
      "Reference",
      "Slip / Transfer ID",
      "Method",
      "Engine No",
      "Chassis No",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.slip_date || row.date,
      row.added_by || "N/A",
      row.customer_name,
      row.ref || "N/A",
      row.slip_id,
      row.payment_method,
      row.engine_number || "N/A",
      row.chassis_number || "N/A",
      toNumber(row.amount),
    ]);
    downloadExcel(
      `Payment Report${filterSummary ? ` (${filterSummary})` : ""}`,
      headers,
      data,
      `Date_Report_${startDate}_${endDate}`,
      calculateTotal(),
      undefined,
      undefined,
      `${reportData.length} Transactions`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  const methodCounts = countByMethod();

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Payment Report by Date</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <User className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — open Filters to pick a date range or add criteria"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL PERIOD REVENUE</p>
                <p className="text-2xl font-bold">{calculateTotal()}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TRANSACTIONS</p>
                <p className="text-2xl font-bold">{reportData.length}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DIRECT PAYMENTS</p>
                <p className="text-2xl font-bold">{methodCounts["Direct"] || 0}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">REMITTANCES</p>
                <p className="text-2xl font-bold">{methodCounts["Remittance"] || 0}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DEALER PAYMENTS</p>
                <p className="text-2xl font-bold">{methodCounts["Dealer Payment"] || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Added By</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Customer / Entity</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Reference</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Slip / Transfer ID</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Method</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Engine No</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Chassis No</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((row, index) => (
                      <TableRow
                        key={`${row.slip_id}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">{row.slip_date || row.date}</TableCell>
                        <TableCell className="px-4 py-3">{row.added_by || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3">{row.customer_name}</TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.ref || row.inquiry_number || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.slip_id}</TableCell>
                        <TableCell className="px-4 py-3 capitalize">{row.payment_method}</TableCell>
                        <TableCell className="px-4 py-3 font-mono text-xs">{row.engine_number || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 font-mono text-xs">{row.chassis_number || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">{formatCurrency(row.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No matching records found for the applied filters.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Specify a time window to generate periodic insights.</p>
          </div>
        </div>
      )}

      <Drawer
        title="Payment Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Added User
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Users"
              value={draftAddedUserId}
              onChange={(val) => setDraftAddedUserId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {users.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.username}
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Customer
            </label>
            <Select
              showSearch
              size="large"
              placeholder="All Customers"
              className="w-full premium-select"
              onSearch={(val) => setCustomerSearch(val)}
              onChange={(val) => setDraftCustomerId(val)}
              value={draftCustomerId}
              allowClear
              filterOption={false}
              notFoundContent={loading ? <Spin size="small" /> : <div className="p-4 text-gray-400 text-sm text-center">No customers found</div>}
            >
              {customerData.map((c) => (
                <Option key={c.id} value={c.id}>
                  <div className="flex justify-between items-center py-1">
                    <span className="font-semibold text-gray-800">{c.name}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-mono">{c.mobile}</span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Inquiry Number
            </label>
            <Input
              size="large"
              placeholder="e.g. PNQNLY2026072407447"
              value={draftInquiryNumber}
              onChange={(e) => setDraftInquiryNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Engine Number
            </label>
            <Input
              size="large"
              placeholder="e.g. PDXCTB80431"
              value={draftEngineNumber}
              onChange={(e) => setDraftEngineNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Chassis Number
            </label>
            <Input
              size="large"
              placeholder="e.g. MD2B54DX5TCB64336"
              value={draftChassisNumber}
              onChange={(e) => setDraftChassisNumber(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Slip / Transfer ID
            </label>
            <Input
              size="large"
              placeholder="e.g. SLIP-000123"
              value={draftSlipId}
              onChange={(e) => setDraftSlipId(e.target.value)}
              allowClear
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Payment Method
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Methods"
              value={draftPaymentMethod}
              onChange={(val) => setDraftPaymentMethod(val || "")}
              allowClear
            >
              {METHOD_OPTIONS.filter((m) => m.value !== "").map((m) => (
                <Option key={m.value} value={m.value}>
                  {m.label}
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};